import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlerts } from '../context/AlertContext';
import { Bell, Zap, Sun, Droplets, MoveVertical, CarFront, Check, Settings, Wifi, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { pt } from 'date-fns/locale';

const categoryIcons = {
  energia: Zap,
  fotovoltaico: Sun,
  agua: Droplets,
  elevadores: MoveVertical,
  postosVE: CarFront,
};

const categoryLabels = {
  energia: 'Energia',
  fotovoltaico: 'Fotovoltaico',
  agua: 'Água',
  elevadores: 'Elevadores',
  postosVE: 'Postos VE',
};

const categoryColors = {
  energia: 'bg-yellow-100 text-yellow-700',
  fotovoltaico: 'bg-orange-100 text-orange-700',
  agua: 'bg-blue-100 text-blue-700',
  elevadores: 'bg-purple-100 text-purple-700',
  postosVE: 'bg-green-100 text-green-700',
};

const severityColors = {
  critical: 'border-l-red-500 bg-red-50',
  warning: 'border-l-yellow-500 bg-yellow-50',
  info: 'border-l-blue-500 bg-blue-50',
};

const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const { alertsByCategory, unreadCount, realAlertsCount, markAsRead, markAllAsRead, refreshWaterAlerts, isLoadingWater } = useAlerts();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5 text-gray-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span>Notificações</span>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unreadCount} não lidas
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {realAlertsCount > 0 && (
              <Badge className="bg-green-100 text-green-700 text-xs">
                <Wifi className="h-3 w-3 mr-1" />
                {realAlertsCount} reais
              </Badge>
            )}
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllAsRead}>
                <Check className="h-3 w-3 mr-1" />
                Marcar
              </Button>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <ScrollArea className="h-[400px]">
          {Object.keys(alertsByCategory).length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sem alertas</p>
              <p className="text-sm">Não existem alertas ativos no momento</p>
            </div>
          ) : (
            Object.entries(alertsByCategory).map(([categoria, categoryAlerts]) => {
              const Icon = categoryIcons[categoria];
              const realCount = categoryAlerts.filter(a => !a.simulado).length;
              return (
                <div key={categoria} className="mb-2">
                  <div className={`px-3 py-2 flex items-center gap-2 ${categoryColors[categoria]} sticky top-0 z-10`}>
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{categoryLabels[categoria]}</span>
                    {realCount > 0 && (
                      <Badge className="bg-green-600 text-white text-xs ml-1">
                        <Wifi className="h-2 w-2 mr-0.5" />
                        Real
                      </Badge>
                    )}
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {categoryAlerts.length}
                    </Badge>
                  </div>
                  {categoryAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`px-3 py-2 border-l-4 mx-2 my-1 rounded-r cursor-pointer transition-colors hover:bg-gray-100 ${severityColors[alert.severidade]} ${!alert.lido ? 'font-medium' : 'opacity-75'}`}
                      onClick={() => markAsRead(alert.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <p className="text-sm truncate">{alert.tipo}</p>
                            {!alert.simulado && (
                              <Wifi className="h-3 w-3 text-green-600 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5">{alert.mensagem}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-gray-400">{alert.local}</span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-400">
                              {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true, locale: pt })}
                            </span>
                            {alert.valor && (
                              <>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs font-medium text-gray-600">{alert.valor}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {!alert.lido && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </ScrollArea>
        
        <DropdownMenuSeparator />
        <div className="flex items-center gap-2 p-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex-1 text-xs" 
            onClick={refreshWaterAlerts}
            disabled={isLoadingWater}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${isLoadingWater ? 'animate-spin' : ''}`} />
            Atualizar Água
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex-1 text-xs" 
            onClick={() => navigate('/configuracoes/alertas')}
          >
            <Settings className="h-3 w-3 mr-1" />
            Configurar
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsDropdown;
